import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  FaAngleLeft,
  FaAngleRight,
  FaEdit,
  FaArrowLeft,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";
import HeaderContainer from "../CrewDashboard/HeaderContainer";

const ManagerVehicles = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [search, setSearch] = useState(null);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [currentManagerId, setCurrentManagerId] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      VehicleStatusID: "",
      Code: "",
      Make: "",
      Model: "",
      PlateNumber: "",
      VIN: "",
      TollTag: "",
      TrackingCode: "",
    },
  });

  useEffect(() => {
    // Get current manager's UserID from session
    const userID = sessionStorage.getItem("UserId");
    setCurrentManagerId(userID);
  }, []);

  useEffect(() => {
    if (vehicle) {
      reset({
        CompanyID: vehicle.CompanyID,
        VehicleStatusID: vehicle.VehicleStatusID,
        Code: vehicle.Code,
        Make: vehicle.Make,
        Model: vehicle.Model,
        PlateNumber: vehicle.PlateNumber,
        VIN: vehicle.VIN || "",
        TollTag: vehicle.TollTag || "",
        TrackingCode: vehicle.TrackingCode || "",
      });
    }
  }, [vehicle, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: Companies } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      setCompanies(Companies || []);
      const { data: Statuses } = await supabase
        .from("VehicleStatuses")
        .select("VehicleStatusID, Description");
      setStatuses(Statuses || []);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      if (!currentManagerId) return;
      
      let query = supabase
        .from("Vehicles")
        .select(
          "VehicleID, CompanyID, VehicleStatusID, Code, Make, Model, PlateNumber, VIN, TollTag, TrackingCode, ManagerID",
          { count: "exact", head: false }
        )
        .eq("ManagerID", currentManagerId)
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      
      const { data, count, error } = await query;
      if (!error) {
        setVehiclesData(data || []);
        setTotalCount(count || 0);
      }
    };
    fetchVehicles();
  }, [page, search, isEdit, currentManagerId]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setVehicle(null);
  }, []);
  
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      VehicleStatusID: statuses[0] ? statuses[0].VehicleStatusID : "",
      Code: "",
      Make: "",
      Model: "",
      PlateNumber: "",
      VIN: "",
      TollTag: "",
      TrackingCode: "",
    });
    setIsEdit(true);
    setVehicle(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setVehicle(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (vehicle) {
        // Update
        const { error } = await supabase
          .from("Vehicles")
          .update({
            CompanyID: data.CompanyID,
            VehicleStatusID: data.VehicleStatusID,
            Code: data.Code,
            Make: data.Make,
            Model: data.Model,
            PlateNumber: data.PlateNumber,
            VIN: data.VIN,
            TollTag: data.TollTag,
            TrackingCode: data.TrackingCode,
            ManagerID: currentManagerId, // Always use current manager's ID
          })
          .eq("VehicleID", vehicle.VehicleID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create - automatically assign to current manager
        const { error } = await supabase.from("Vehicles").insert({
          CompanyID: data.CompanyID,
          VehicleStatusID: data.VehicleStatusID,
          Code: data.Code,
          Make: data.Make,
          Model: data.Model,
          PlateNumber: data.PlateNumber,
          VIN: data.VIN,
          TollTag: data.TollTag,
          TrackingCode: data.TrackingCode,
          ManagerID: currentManagerId, // Automatically assign to current manager
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [vehicle, onError, onSuccess, currentManagerId]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  const handleBack = () => {
    navigate("/crew-dashboard");
  };

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      <div className="mt-5">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button mb-4"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        
        {isEdit ? (
          <div className="driver-patent-edit-content mt-4 rounded-lg">
            <h5 className="mb-4">{vehicle ? "Edit Vehicle" : "Add New Vehicle"}</h5>
            <div className="flex justify-between mb-2">
              <div>Company</div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="CompanyID"
                  rules={{ required: "Required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg"
                      value={value}
                      onChange={onChange}
                      error={errors.CompanyID?.message}
                    >
                      {companies.map((company) => (
                        <option key={company.CompanyID} value={company.CompanyID}>
                          {company.Name}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Status</div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="VehicleStatusID"
                  rules={{ required: "Required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg"
                      value={value}
                      onChange={onChange}
                      error={errors.VehicleStatusID?.message}
                    >
                      {statuses.map((status) => (
                        <option
                          key={status.VehicleStatusID}
                          value={status.VehicleStatusID}
                        >
                          {status.Description}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Code</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="Code"
                  rules={{ required: "Code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.Code?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Make</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="Make"
                  rules={{ required: "Make is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.Make?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Model</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="Model"
                  rules={{ required: "Model is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.Model?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Plate Number</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="PlateNumber"
                  rules={{ required: "Plate Number is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.PlateNumber?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>VIN</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="VIN"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Toll Tag</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="TollTag"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>Tracking Code</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="TrackingCode"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-4 save-button">
              <button
                disabled={false}
                onClick={handleSubmit(OnCreate)}
                className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
              >
                <span>{vehicle ? "Update" : "Create"}</span>
              </button>
              <div>
                <div
                  onClick={OnCancel}
                  className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                >
                  <span>Cancel</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex ">
              <h5 className="mb-0">My Vehicles</h5>
              <button
                className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
                onClick={onAddNew}
              >
                <span>Add New</span>
              </button>
            </div>
            <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
            <table className="table-auto w-full mt-4 border border-gray-200">
              <thead>
                <tr className="bg-gray-100 cursor-pointer">
                  <th className="px-4 py-2 border">ID</th>
                  <th className="px-4 py-2 border">Company</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Code</th>
                  <th className="px-4 py-2 border">Make</th>
                  <th className="px-4 py-2 border">Model</th>
                  <th className="px-4 py-2 border">Plate Number</th>
                  <th className="px-4 py-2 border">VIN</th>
                  <th className="px-4 py-2 border">Toll Tag</th>
                  <th className="px-4 py-2 border">Tracking Code</th>
                  <th className="px-4 py-2 border">Edit</th>
                </tr>
              </thead>
              <tbody>
                {vehiclesData &&
                  vehiclesData.map((vehicle) => (
                    <tr key={vehicle.VehicleID} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{vehicle.VehicleID}</td>
                      <td className="px-4 py-2 border">
                        {companies.find((c) => c.CompanyID === vehicle.CompanyID)
                          ?.Name || ""}
                      </td>
                      <td className="px-4 py-2 border">
                        {statuses.find(
                          (s) => s.VehicleStatusID === vehicle.VehicleStatusID
                        )?.Description || ""}
                      </td>
                      <td className="px-4 py-2 border">{vehicle.Code}</td>
                      <td className="px-4 py-2 border">{vehicle.Make}</td>
                      <td className="px-4 py-2 border">{vehicle.Model}</td>
                      <td className="px-4 py-2 border">{vehicle.PlateNumber}</td>
                      <td className="px-4 py-2 border">{vehicle.VIN || ""}</td>
                      <td className="px-4 py-2 border">
                        {vehicle.TollTag || ""}
                      </td>
                      <td className="px-4 py-2 border">
                        {vehicle.TrackingCode || ""}
                      </td>
                      <td className="px-4 py-2 border cursor-point">
                        <FaEdit
                          onClick={() => {
                            setVehicle(vehicle);
                            setIsEdit(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {vehiclesData.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No vehicles assigned to you yet. Click "Add New" to add a vehicle.
              </div>
            )}
            <div className="pagination-container">
              <div className="pagination-content">
                <div>
                  <p className="pagination-text">
                    Showing <span>{totalCount > 0 ? (page - 1) * PAGE_SIZE + 1 : 0}</span> to{" "}
                    <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                    <span>{totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="pagination-nav">
                    <button
                      onClick={onPrevPage}
                      disabled={page === 1}
                      className={`pagination-item ${
                        page === 1 ? "disabled" : ""
                      }`}
                    >
                      <FaAngleLeft className="pagination-icon" />
                    </button>
                    <button
                      onClick={onNextPage}
                      disabled={page * PAGE_SIZE >= totalCount}
                      className={`pagination-item ${
                        page * PAGE_SIZE >= totalCount ? "disabled" : ""
                      }`}
                    >
                      <FaAngleRight className="pagination-icon" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerVehicles;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"Code"}>Code</option>
              <option value={"Make"}>Make</option>
              <option value={"Model"}>Model</option>
              <option value={"PlateNumber"}>Plate Number</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Code", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};